import {
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { Tax, TransactionFull } from "../models";
import { ApprovalStatus, CurrencyCode } from "../API";
import { useApprovalDecisions } from "../hooks/approvals/useApprovalDecisions";
import { formatAmount, formatDate, getTaxAmount, getUserName } from "../utils";
import { stringWithoutManualLineBreaks } from "../utils";
import StatusChip from "./status-chip";
import ReceiptPreview from "../routes/dashboard/approvals/receipt-preview";
import { Select, Option as SelectOption } from "./select";
import { isActionableStatus } from "../utils/approval-status-helpers";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DrawerMode = "employee" | "approver";

export type TaxFieldChange = {
  taxId: string;
  taxRateId?: string;
  rate?: number;
  amount?: number;
};

export type ResubmitFieldChanges = {
  amount?: number;
  categoryId?: string;
  paidAt?: string;
  description?: string;
  taxes?: TaxFieldChange[];
};

type TransactionDetailDrawerProps = {
  transaction: TransactionFull;
  isOpen: boolean;
  onClose: () => void;
  mode: DrawerMode;
  // Employee-specific
  onResubmit?: (id: string, changes: ResubmitFieldChanges) => void;
  isResubmittable?: boolean;
  bookingAccounts?: { id: string; name: string }[];
  availableTaxRates?: { id: string; code: string; rate: number }[];
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Flex width="full" justifyContent="space-between" alignItems="center">
    <Text fontSize="sm" color="gray.600">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="500">
      {value}
    </Text>
  </Flex>
);

const TaxRow = ({
  tax,
  transaction,
}: {
  tax: Tax;
  transaction: TransactionFull;
}) => (
  <Flex width="full" justifyContent="space-between" alignItems="center">
    <Text fontSize="sm" color="gray.600">
      MwSt. {tax.taxRate.rate}%
    </Text>
    <HStack spacing="1">
      <Text fontSize="sm" fontWeight="500">
        {formatAmount(getTaxAmount(transaction, tax), CurrencyCode.chf)}
      </Text>
      <Text fontSize="sm" color="gray.400">
        von{" "}
        {formatAmount(
          tax.totalAmount ?? transaction.amount,
          CurrencyCode.chf
        )}
      </Text>
    </HStack>
  </Flex>
);

type EditableTaxRowProps = {
  taxId: string;
  taxRateId: string;
  rate: number;
  amount: number;
  totalAmount: number;
  currencyCode: CurrencyCode;
  availableTaxRates?: { id: string; code: string; rate: number }[];
  onTaxRateSelect: (taxId: string, taxRateId: string, rate: number) => void;
};

const EditableTaxRow = ({
  taxId,
  taxRateId,
  amount,
  totalAmount,
  currencyCode,
  availableTaxRates,
  onTaxRateSelect,
}: EditableTaxRowProps) => {
  const taxRateOptions: SelectOption[] = (availableTaxRates ?? []).map(
    (tr) => ({
      value: tr.id,
      label: `${tr.code} \u2014 ${tr.rate}%`,
    })
  );

  const selectedOption = taxRateOptions.find((o) => o.value === taxRateId);

  return (
    <FormControl>
      <FormLabel fontSize="xs" color="gray.600">
        MwSt.
      </FormLabel>
      <HStack spacing="2">
        <Box width="44">
          <Select
            value={selectedOption}
            options={taxRateOptions}
            onChange={(option) => {
              if (option) {
                const selected = availableTaxRates?.find(
                  (tr) => tr.id === option.value
                );
                if (selected) {
                  onTaxRateSelect(taxId, selected.id, selected.rate);
                }
              }
            }}
          />
        </Box>
        <Text fontSize="sm" fontWeight="500">
          {formatAmount(amount, currencyCode)}
        </Text>
        <Text fontSize="sm" color="gray.500">
          {currencyCode.toUpperCase()}
        </Text>
      </HStack>
      <Text fontSize="xs" color="gray.400" mt="1">
        von {formatAmount(totalAmount, currencyCode)}
      </Text>
    </FormControl>
  );
};

const getApprovalReasonLabel = (triggers: string | null | undefined): string | null => {
  if (!triggers) return null;
  try {
    const parsed = JSON.parse(triggers);
    if (parsed.type === "threshold" || parsed.type === "amountThreshold") {
      const currency = String(parsed.currency ?? "CHF");
      const value = String(parsed.value ?? parsed.amountThreshold ?? "");
      return `Über ${currency} ${value}`;
    }
    if (parsed.type === "bookingAccount" || parsed.type === "category") {
      const name = String(parsed.categoryName ?? parsed.bookingAccountName ?? "");
      return `Kategorie: ${name}`;
    }
    return null;
  } catch {
    return null;
  }
};

const approvalBannerConfig: Record<
  string,
  { bg: string; borderColor: string; note: string }
> = {
  [ApprovalStatus.pending]: {
    bg: "blue.50",
    borderColor: "blue.400",
    note: "Warte auf Freigabe",
  },
  [ApprovalStatus.changesRequested]: {
    bg: "orange.50",
    borderColor: "orange.400",
    note: "",
  },
  [ApprovalStatus.rejected]: {
    bg: "red.50",
    borderColor: "red.400",
    note: "",
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TransactionDetailDrawer = ({
  transaction,
  isOpen,
  onClose,
  mode,
  onResubmit,
  isResubmittable,
  bookingAccounts,
  availableTaxRates,
}: TransactionDetailDrawerProps) => {
  // Fetch approval decisions for this transaction
  const { data: approvalDecisions } = useApprovalDecisions(
    transaction.approvalStatus &&
      transaction.approvalStatus !== ApprovalStatus.notRequired
      ? transaction.id
      : undefined
  );
  const latestDecision = useMemo(() => {
    if (!approvalDecisions || approvalDecisions.length === 0) return null;
    // Copy before sort (.sort mutates) and prefer semantic timestamps:
    // decidedAt for approved/rejected/changesRequested, requestedAt for pending,
    // createdAt as final fallback.
    return [...approvalDecisions].sort((a, b) => {
      const aTime = new Date(
        a.decidedAt ?? a.requestedAt ?? a.createdAt
      ).getTime();
      const bTime = new Date(
        b.decidedAt ?? b.requestedAt ?? b.createdAt
      ).getTime();
      return bTime - aTime;
    })[0];
  }, [approvalDecisions]);

  // Memoize parsed reason label so JSON.parse runs once per triggers change
  const approvalReasonLabel = useMemo(
    () => getApprovalReasonLabel(latestDecision?.triggers),
    [latestDecision?.triggers]
  );

  // Editable field state
  const [editedAmount, setEditedAmount] = useState<number>(
    transaction.amount ?? 0
  );
  const [editedCategoryId, setEditedCategoryId] = useState<string>(
    transaction.bookingAccountId ?? ""
  );
  const [editedPaidAt, setEditedPaidAt] = useState<string>(
    transaction.paidAt ?? ""
  );
  const [editedDescription, setEditedDescription] = useState<string>(
    transaction.title ?? ""
  );
  const [editedTaxes, setEditedTaxes] = useState<
    Record<string, { taxRateId: string; rate: number; amount: number }>
  >({});

  // Reset editable state ONLY when user navigates to a different transaction.
  // Per 04-03 plan: "useEffect on transaction.id or a resubmit flag".
  // Including other field deps caused in-progress edits to be wiped on
  // unrelated cache invalidations.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setEditedAmount(transaction.amount ?? 0);
    setEditedCategoryId(transaction.bookingAccountId ?? "");
    setEditedPaidAt(transaction.paidAt ?? "");
    setEditedDescription(transaction.title ?? "");

    // Initialize editable tax state from active taxes
    const taxState: Record<string, { taxRateId: string; rate: number; amount: number }> = {};
    for (const tax of transaction.taxes.filter((t) => !t.deletedAt)) {
      taxState[tax.id] = {
        taxRateId: tax.taxRateId,
        rate: tax.taxRate.rate,
        amount: getTaxAmount(transaction, tax),
      };
    }
    setEditedTaxes(taxState);
  }, [transaction.id]);

  // Derived tax / currency / reference flags for field display
  const activeTaxes = transaction.taxes.filter((t) => !t.deletedAt);
  const hasTaxes = activeTaxes.length > 0;
  const isMultiCurrency = transaction.currencyCode !== CurrencyCode.chf;
  const hasReference = !!transaction.transactionReference?.name;

  const approvalStatus = transaction.approvalStatus;
  const showApprovalSection = isActionableStatus(approvalStatus);

  const isEditing = isResubmittable === true;

  const bannerConfig =
    approvalStatus &&
    approvalStatus !== ApprovalStatus.notRequired &&
    approvalStatus !== ApprovalStatus.approved
      ? approvalBannerConfig[approvalStatus as keyof typeof approvalBannerConfig]
      : null;

  // Build receipt preview adapter
  const realAttachments = transaction.attachments ?? [];
  const receiptTransaction = {
    id: transaction.id,
    title: transaction.title ?? "Transaktion",
    paidAt: transaction.paidAt,
    currencyCode: transaction.currencyCode,
    amount: transaction.amount,
    attachments: realAttachments,
  };

  const handleResubmitClick = () => {
    const changes: ResubmitFieldChanges = {};
    if (editedAmount !== transaction.amount) changes.amount = editedAmount;
    if (editedCategoryId !== (transaction.bookingAccountId ?? ""))
      changes.categoryId = editedCategoryId;
    if (editedPaidAt !== (transaction.paidAt ?? ""))
      changes.paidAt = editedPaidAt;
    if (editedDescription !== (transaction.title ?? ""))
      changes.description = editedDescription;

    // Build tax changes by comparing edited values against originals
    const taxChanges: TaxFieldChange[] = [];
    for (const tax of activeTaxes) {
      const edited = editedTaxes[tax.id];
      if (!edited) continue;
      const origRate = tax.taxRate.rate;
      const origAmount = getTaxAmount(transaction, tax);
      if (edited.rate !== origRate || edited.amount !== origAmount) {
        taxChanges.push({
          taxId: tax.id,
          taxRateId: edited.taxRateId,
          ...(edited.rate !== origRate ? { rate: edited.rate } : {}),
          ...(edited.amount !== origAmount ? { amount: edited.amount } : {}),
        });
      }
    }
    if (taxChanges.length > 0) changes.taxes = taxChanges;

    onResubmit?.(transaction.id, changes);
  };

  // Category select options
  const categoryOptions: SelectOption[] | undefined = bookingAccounts?.map(
    (ba) => ({
      value: ba.id,
      label: ba.name,
    })
  );

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <Text fontSize="lg" fontWeight="semibold">
            {transaction.title || "Transaktion"}
          </Text>
        </DrawerHeader>

        <DrawerBody>
          <VStack spacing="3" alignItems="start" pt="4">
            {/* ---------- Approval Banner ---------- */}
            {showApprovalSection && bannerConfig && (
              <>
                <Box
                  width="full"
                  p="3"
                  bg={bannerConfig.bg}
                  borderRadius="md"
                  borderLeft="3px solid"
                  borderLeftColor={bannerConfig.borderColor}
                >
                  {approvalStatus === ApprovalStatus.pending ? (
                    <VStack spacing="1" alignItems="start">
                      <StatusChip type="approval" status={approvalStatus} />
                      <Text fontSize="xs" color="gray.500">
                        {bannerConfig.note}
                        {latestDecision?.requestedAt &&
                          ` · Eingereicht ${formatDate(new Date(latestDecision.requestedAt))}`}
                      </Text>
                    </VStack>
                  ) : (
                    <VStack spacing="1.5" alignItems="start">
                      {/* Status chip + reason label */}
                      <HStack spacing="2" width="full" justifyContent="space-between">
                        <StatusChip type="approval" status={approvalStatus!} />
                        {approvalReasonLabel && (
                          <Text fontSize="xs" color="gray.500">
                            {approvalReasonLabel}
                          </Text>
                        )}
                      </HStack>

                      {/* Approver comment */}
                      {latestDecision?.comment && (
                        <Text fontSize="sm" color="gray.700" fontStyle="italic">
                          &ldquo;{latestDecision.comment}&rdquo;
                        </Text>
                      )}

                      {/* Approver + date on one line */}
                      <Text fontSize="xs" color="gray.500">
                        {latestDecision?.reviewerUserId ?? "—"}
                        {latestDecision?.decidedAt &&
                          ` · ${formatDate(new Date(latestDecision.decidedAt))}`}
                      </Text>
                    </VStack>
                  )}
                </Box>

                <Divider />
              </>
            )}

            {/* ---------- Transaction Detail Fields ---------- */}
            {isEditing ? (
              <VStack spacing="3" width="full" alignItems="start">
                {/* 1. Datum — editable */}
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.600">
                    Datum
                  </FormLabel>
                  <Input
                    type="date"
                    size="sm"
                    value={editedPaidAt?.split("T")[0] ?? ""}
                    onChange={(e) => setEditedPaidAt(e.target.value)}
                  />
                </FormControl>

                {/* 2. Beschreibung — editable */}
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.600">
                    Beschreibung
                  </FormLabel>
                  <Input
                    size="sm"
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                  />
                </FormControl>

                {/* 3. Betrag / Original Betrag — editable */}
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.600">
                    {isMultiCurrency
                      ? `Original Betrag (${transaction.currencyCode.toUpperCase()})`
                      : `Betrag (${transaction.currencyCode.toUpperCase()})`}
                  </FormLabel>
                  <NumberInput
                    size="sm"
                    value={editedAmount}
                    onChange={(_, val) => setEditedAmount(val)}
                    precision={2}
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>

                {/* 4. CHF Betrag — read-only, conditional */}
                {isMultiCurrency && (
                  <DetailRow
                    label="CHF Betrag"
                    value={formatAmount(
                      transaction.baseCurrencyAmount ?? transaction.amount,
                      CurrencyCode.chf
                    )}
                  />
                )}

                {/* 5. MwSt. — one row per tax, editable via Select dropdown */}
                {hasTaxes &&
                  activeTaxes.map((tax) => {
                    const edited = editedTaxes[tax.id];
                    return (
                      <EditableTaxRow
                        key={tax.id}
                        taxId={tax.id}
                        taxRateId={edited?.taxRateId ?? tax.taxRateId}
                        rate={edited?.rate ?? tax.taxRate.rate}
                        amount={
                          edited?.amount ?? getTaxAmount(transaction, tax)
                        }
                        totalAmount={tax.totalAmount ?? transaction.amount}
                        currencyCode={CurrencyCode.chf}
                        availableTaxRates={availableTaxRates}
                        onTaxRateSelect={(taxId, taxRateId, rate) => {
                          setEditedTaxes((prev) => {
                            const totalAmount =
                              activeTaxes.find((t) => t.id === taxId)
                                ?.totalAmount ?? transaction.amount;
                            const computedAmount =
                              Math.round(
                                totalAmount * (rate / (100 + rate)) * 100
                              ) / 100;
                            return {
                              ...prev,
                              [taxId]: {
                                taxRateId,
                                rate,
                                amount: computedAmount,
                              },
                            };
                          });
                        }}
                      />
                    );
                  })}

                {/* 6. Buchungskategorie — editable */}
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.600">
                    Buchungskategorie
                  </FormLabel>
                  {categoryOptions && categoryOptions.length > 0 ? (
                    <Select
                      value={categoryOptions.find(
                        (o) => o.value === editedCategoryId
                      )}
                      options={categoryOptions}
                      onChange={(option) => {
                        if (option)
                          setEditedCategoryId(option.value as string);
                      }}
                    />
                  ) : (
                    <Text fontSize="sm" color="gray.700">
                      {stringWithoutManualLineBreaks(
                        transaction.bookingAccount?.name ?? ""
                      )}
                    </Text>
                  )}
                </FormControl>

                {/* 9. Referenz — read-only, conditional */}
                {hasReference && (
                  <DetailRow
                    label="Referenz"
                    value={transaction.transactionReference!.name}
                  />
                )}

                {/* 10. Mitarbeiter — read-only */}
                <DetailRow
                  label="Mitarbeiter"
                  value={
                    transaction.user
                      ? getUserName(transaction.user)
                      : "Unbekannt"
                  }
                />
              </VStack>
            ) : (
              <VStack spacing="3" width="full" alignItems="start">
                {/* 1. Datum */}
                <DetailRow
                  label="Datum"
                  value={formatDate(new Date(transaction.paidAt))}
                />

                {/* 2. Beschreibung */}
                <DetailRow
                  label="Beschreibung"
                  value={transaction.title ?? ""}
                />

                {/* 3. Betrag / Original Betrag */}
                <DetailRow
                  label={isMultiCurrency ? "Original Betrag" : "Betrag"}
                  value={formatAmount(
                    transaction.amount,
                    transaction.currencyCode
                  )}
                />

                {/* 4. CHF Betrag — conditional */}
                {isMultiCurrency && (
                  <DetailRow
                    label="CHF Betrag"
                    value={formatAmount(
                      transaction.baseCurrencyAmount ?? transaction.amount,
                      CurrencyCode.chf
                    )}
                  />
                )}

                {/* 5. MwSt. — one row per tax, conditional */}
                {hasTaxes &&
                  activeTaxes.map((tax) => (
                    <TaxRow
                      key={tax.id}
                      tax={tax as Tax}
                      transaction={transaction}
                    />
                  ))}

                {/* 6. Buchungskategorie */}
                <DetailRow
                  label="Buchungskategorie"
                  value={stringWithoutManualLineBreaks(
                    transaction.bookingAccount?.name ?? ""
                  )}
                />

                {/* 9. Referenz — conditional */}
                {hasReference && (
                  <DetailRow
                    label="Referenz"
                    value={transaction.transactionReference!.name}
                  />
                )}

                {/* 10. Mitarbeiter */}
                <DetailRow
                  label="Mitarbeiter"
                  value={
                    transaction.user
                      ? getUserName(transaction.user)
                      : "Unbekannt"
                  }
                />
              </VStack>
            )}

            <Divider />

            {/* ---------- Receipt Preview ---------- */}
            {realAttachments.length > 0 ? (
              <ReceiptPreview
                attachments={realAttachments}
                transaction={receiptTransaction}
              />
            ) : (
              <Text fontSize="sm" color="gray.400">
                Kein Beleg vorhanden
              </Text>
            )}
          </VStack>
        </DrawerBody>

        {/* ---------- Footer ---------- */}
        {mode === "employee" && isResubmittable && (
            <DrawerFooter borderTopWidth="1px">
              <Button colorScheme="primary" onClick={handleResubmitClick}>
                Erneut einreichen
              </Button>
            </DrawerFooter>
          )}
      </DrawerContent>
    </Drawer>
  );
};

export default TransactionDetailDrawer;
